import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import withStyles from 'react-jss';
import { map, without, isEmpty } from 'ramda';
import { Checkbox, CheckboxProps, Container, Button } from 'semantic-ui-react';

import { ClassProp } from 'helpers/types';
import { useSelectedPlaces } from 'helpers/useSelectedPlaces';

import { Place } from 'scenes/Tier3VenueOnboarding/Tier3VenueOnboarding.helpers';
import OnboardVenues from 'components/VenueOnboarding/OnboardSelectedVenues';
import AddMissingVenueModal from 'components/VenueOnboarding/AddMissingVenueModal';

import styles from './VenuesList.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;
type Props = {
  classes: Styles;
  places: Place[];
  hasNextPage: boolean;
  addMissingPlace: (newPlace: Place) => void;
  getMore: Function;
};

const renderPlace = (
  classes: Styles,
  selectedPlaces: string[],
  handleSelectPlace: any,
) => (place: Place) => {
  if (!place) return null;

  const {
    externalId,
    name,
    address: { street, city, postal },
  } = place;

  return (
    <li key={externalId} className={classes.venueItem}>
      <Checkbox
        checked={selectedPlaces.includes(externalId)}
        value={externalId}
        onChange={handleSelectPlace}
        id={externalId}
      />
      <label htmlFor={externalId}>
        <div className={classes.venueContainer}>
          <p className={classes.venueName}>{name}</p>
          <p
            className={classes.venueAddress}
          >{`${street}, ${city} ${postal}`}</p>
        </div>
      </label>
    </li>
  );
};

const VenuesList: FunctionComponent<Props> = ({
  classes,
  places,
  hasNextPage,
  getMore,
  addMissingPlace,
}) => {
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const { selectedPlaces, writeSelectedPlacesCache } = useSelectedPlaces();

  const getIdsFromPlaces = (places: Place[]) =>
    places.map(({ externalId }) => externalId);

  const handleSelectPlace = (
    _: SyntheticEvent<HTMLElement, Event>,
    { value: selectedExternalId, checked: selected }: CheckboxProps,
  ) => {
    let newSelectedPlaces = [] as string[];
    if (selected) {
      newSelectedPlaces = [...selectedPlaces, selectedExternalId as string];
    } else {
      newSelectedPlaces = without(
        [selectedExternalId],
        selectedPlaces,
      ) as string[];
    }

    writeSelectedPlacesCache({ data: { selectedPlaces: newSelectedPlaces } });
  };

  const handleSelectAllPlaces = (
    _: SyntheticEvent<HTMLElement, Event>,
    { checked: selectedAll }: CheckboxProps,
  ) => {
    const selectAllPlaces = selectedAll ? getIdsFromPlaces(places) : [];
    writeSelectedPlacesCache({ data: { selectedPlaces: selectAllPlaces } });
    setAllSelected(selectedAll as boolean);
  };

  const HandleAddMissingPlace = (newVenue: Place, venueId: string) => {
    addMissingPlace(newVenue);
    writeSelectedPlacesCache({
      data: { selectedPlaces: [...selectedPlaces, venueId] },
    });
  };

  const handleGetMorePlaces = (_: SyntheticEvent<HTMLElement, Event>) =>
    getMore();

  return (
    <Container>
      <div className={classes.headerContainer}>
        <span className={classes.selectionCount}>
          {' '}
          {selectedPlaces.length} of {places.length} selected{' '}
        </span>
        <Checkbox
          checked={allSelected}
          onChange={handleSelectAllPlaces}
          label="Select All"
        />
      </div>
      {!isEmpty(places) ? (
        <>
          <AddMissingVenueModal onAddPlace={HandleAddMissingPlace} />
          <ul className={classes.venueList}>
            {map(
              renderPlace(classes, selectedPlaces, handleSelectPlace),
              places,
            )}
          </ul>
          <div className={classes.buttonContainer}>
            {hasNextPage && (
              <Button className={classes.button} onClick={handleGetMorePlaces}>
                Get More
              </Button>
            )}
            <OnboardVenues places={places} placeIds={selectedPlaces} />
          </div>
        </>
      ) : (
        <>
          <p className={classes.noVenue}>
            We were unable to locate any venues, please feel free to add your
            venues manually.
          </p>
          <div className={classes.noVenueWrapper}>
            <AddMissingVenueModal
              onAddPlace={HandleAddMissingPlace}
              noVenuesFound
            />
          </div>
        </>
      )}
    </Container>
  );
};

export default withStyles(styles)(VenuesList);
